import { useMemo } from 'react';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import type { NullableDateInput } from '@localization/formatters/dateFormatter';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { resolveEventDateDisplay } from './resolveEventDateDisplay';
import { eventDateMetadataToContent } from './eventDateMetadata';
import { withSingleRangeBreakPoint } from './withSingleRangeBreakPoint';

type UseEventLevelDateDisplayArgs = Readonly<{
  // Range START — the dedicated EventInfo.startDate override. When null the range is suppressed (see below):
  // callers must NOT substitute the canonical event date, which is a date within the event, not its start.
  startTimestamp: NullableDateInput;
  timezone?: string | null;
  // Event-level date config: EventInfo.dateMetadata and EventInfo.endDate.timestamp (PRNT-4548).
  dateMetadata?: Parameters<typeof eventDateMetadataToContent>[0];
  endDateTimestamp?: string | null;
}>;

// Resolves the event-level multi-day date override (a saved range / custom text) from EventInfo. Flag-gated
// behind websiteMultiDayEventDateEnabled: returns undefined when the flag is off (or no range is configured),
// so callers fall back to their existing single-date string (purely additive). This deliberately gates
// *display* on the flag as a rollout safety valve — a stricter stance than the "saved data always renders"
// convention — so a segment can be rolled back cleanly while the feature stabilizes; drop the gate once the
// flag is retired. Unlike useWelcomeEventDateDisplay this reads only the event-level source (no welcome-page
// blob fetch), so it is usable from both admin and guest.
//
// `localized` renders in the event locale. `canonicalEn` is the same resolution pinned to en-US, for analytics
// payloads: telemetry fields that are grouped or filtered on must stay comparable across event locales, so a
// caller that logs the date needs a locale-stable value alongside the one it renders. Both come from a single
// feature-flag read so the exposure isn't double-counted, which two separate hook calls would risk.
//
// `isLoading` reflects the remote flag still resolving: until it settles both values may still flip from
// undefined (single-date fallback) to the range, so callers that PERSIST or navigate on the resolved value
// (e.g. the guest contact collector, which writes it into shared Context before Done renders) must wait for
// it to clear. Live-rendering callers can ignore it.
export const useEventLevelDateDisplayVariants = ({
  startTimestamp,
  timezone,
  dateMetadata,
  endDateTimestamp,
}: UseEventLevelDateDisplayArgs): Readonly<{ localized: string | undefined; canonicalEn: string | undefined; isLoading: boolean }> => {
  const [isMultiDayEnabled, isMultiDayLoading] = useIsFeatureEnabled('websiteMultiDayEventDateEnabled');
  const { formatDate, formatEventDateRange } = useEventLocaleFormatter();
  const { formatDate: formatDateEn, formatEventDateRange: formatEventDateRangeEn } = useEventLocaleFormatter({ language: 'en-US' });
  const variants = useMemo(() => {
    if (!isMultiDayEnabled || dateMetadata?.dateDisplayType !== 'dateRange') {
      return { localized: undefined, canonicalEn: undefined };
    }
    const content = eventDateMetadataToContent(dateMetadata, endDateTimestamp);
    // Without a real start we can't form a correct range — the canonical event date is a date *within* the
    // event, not its start, so fabricating a start from it would show a wrong range. Return undefined so the
    // caller falls back to the single date. Custom text carries no dates, so it still renders.
    if (startTimestamp == null && content.dateRangeFormat !== 'custom') {
      return { localized: undefined, canonicalEn: undefined };
    }
    const options = { titlecase: true, timezone };
    const localized = resolveEventDateDisplay(content, startTimestamp, options, { formatDate, formatEventDateRange });
    // Custom text is excluded: it's the couple's own prose, so a hyphen in it is theirs, not a separator.
    const isFormattedRange = content.dateRangeFormat !== 'custom';
    return {
      // Rendered copy only (see withSingleRangeBreakPoint): the non-breaking spaces would fragment
      // `canonicalEn`'s analytics field across two spellings of the same date.
      localized: isFormattedRange && localized ? withSingleRangeBreakPoint(localized) : localized,
      canonicalEn: resolveEventDateDisplay(content, startTimestamp, options, { formatDate: formatDateEn, formatEventDateRange: formatEventDateRangeEn }),
    };
  }, [isMultiDayEnabled, dateMetadata, endDateTimestamp, startTimestamp, timezone, formatDate, formatEventDateRange, formatDateEn, formatEventDateRangeEn]);
  return useMemo(() => ({ ...variants, isLoading: isMultiDayLoading }), [variants, isMultiDayLoading]);
};

// Display-only shorthand for the callers that don't log the date (guest registry header, website surfaces).
export const useEventLevelDateDisplay = (args: UseEventLevelDateDisplayArgs): string | undefined => useEventLevelDateDisplayVariants(args).localized;
